

import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Briefcase, CheckCircle, AlertTriangle, Users, Lock, ChevronDown } from 'lucide-react';
import TypingAnimation from './TypingAnimation';

interface ConsentDialogProps {
  isOpen: boolean;
  onAgree: (optIn: boolean) => void;
}

const ConsentDialog: FC<ConsentDialogProps> = ({ isOpen, onAgree }) => {
  const [optInRecruiterContact, setOptInRecruiterContact] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [showTyping, setShowTyping] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setShowTyping(true);
      setShowContent(false);
      setHasScrolledToBottom(false);
      // Show content after typing animation completes
      const timer = setTimeout(() => {
        setShowTyping(false);
        setShowContent(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      // More generous tolerance and check if we're near the bottom
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  // Debounced scroll handler for better performance
  const debouncedScrollHandler = useRef<NodeJS.Timeout | null>(null);
  
  const handleScrollDebounced = () => {
    if (debouncedScrollHandler.current) {
      clearTimeout(debouncedScrollHandler.current);
    }
    debouncedScrollHandler.current = setTimeout(() => {
      handleScroll();
    }, 50); // 50ms debounce for smooth detection
  };

  // Check scroll position when content loads
  useEffect(() => {
    if (showContent) {
      setTimeout(() => {
        handleScroll();
      }, 200); // Increased delay to ensure content is fully rendered
    }
  }, [showContent]);

  // Add wheel event listener for better scroll detection
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer && showContent) {
      const handleWheel = () => {
        // Force check scroll position after wheel events
        setTimeout(() => {
          handleScroll();
        }, 10);
      };

      scrollContainer.addEventListener('wheel', handleWheel, { passive: true });
      return () => {
        scrollContainer.removeEventListener('wheel', handleWheel);
      };
    }
  }, [showContent]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debouncedScrollHandler.current) {
        clearTimeout(debouncedScrollHandler.current);
      }
    };
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[450px] h-[600px] bg-main-bg text-foreground border-border shadow-2xl rounded-xl overflow-hidden p-0 flex flex-col">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="flex flex-col h-full"
        >
          {/* Fixed Header */}
          <DialogHeader className="p-4 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border-b border-border/50 flex-shrink-0">
            <div className="flex items-center justify-center space-x-3 mb-3">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              >
                <div className="p-2 bg-primary/20 rounded-full">
                  <ShieldCheck className="h-6 w-6 text-primary" />
                </div>
              </motion.div>
              <div className="text-center">
                {showTyping ? (
                  <TypingAnimation
                    text="Welcome to vTech Labs"
                    speed={60}
                    className="text-xl font-bold text-foreground"
                    onComplete={() => {
                      setTimeout(() => setShowTyping(false), 300);
                      setTimeout(() => setShowContent(true), 600);
                    }}
                  />
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-xl font-bold text-foreground"
                  >
                    Welcome to vTech Labs
                  </motion.div>
                )}
              </div>
            </div>
            <DialogDescription className="text-center text-muted-foreground text-sm">
              Professional Resume Analysis & AI-Powered Career Tools
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable Content Area */}
          <div className="flex-1 min-h-0 relative">
            {/* Scroll Indicator - Bottom */}
            {!hasScrolledToBottom && showContent && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20"
              >
                <div className="flex items-center space-x-2 px-4 py-2 bg-primary/15 border border-primary/30 rounded-full text-sm text-primary font-medium shadow-lg backdrop-blur-sm">
                  <motion.div
                    animate={{ y: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-1.5 h-1.5 rounded-full"
                  />
                  <span className="text-foreground">Scroll down to continue</span>
                  <ChevronDown className="h-4 w-4" />
                </div>
              </motion.div>
            )}

                         {/* Scrollable Content */}
             <div 
               ref={scrollContainerRef}
               onScroll={handleScrollDebounced}
               className="h-full overflow-y-auto p-4 space-y-4"
               style={{ scrollbarWidth: 'thin', scrollbarColor: 'hsl(var(--border)) transparent' }}
             >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-4"
              >
                {/* Professional Introduction */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-foreground text-sm mb-1">Professional AI-Powered Analysis</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Our advanced AI technology provides comprehensive resume analysis, personalized feedback, 
                        and professional insights to enhance your career opportunities.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-accent/5 rounded-lg border border-accent/20">
                    <Lock className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-foreground text-sm mb-1">Enterprise-Grade Security</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Your data is protected with industry-standard encryption and privacy measures. 
                        We maintain strict compliance with data protection regulations.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="">
                  <h3 className=" font-semibold text-foreground flex items-center">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mr-2" />
                    Terms of Service & Privacy Policy
                  </h3>
                  
                  <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
                    <p>
                      By proceeding, you acknowledge and agree to the following terms:
                    </p>
                    <ul className="space-y-1.5 ml-3">
                      <li className="flex items-start space-x-2">
                        <span className="text-primary font-semibold">•</span>
                        <span>This is an experimental platform for AI-powered career tools and resume analysis</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary font-semibold">•</span>
                        <span>Your resume data will be processed securely for analysis and feedback generation</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary font-semibold">•</span>
                        <span>Analysis results and feedback are provided for educational and career development purposes</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary font-semibold">•</span>
                        <span>We do not guarantee job placement or specific career outcomes</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary font-semibold">•</span>
                        <span>You may withdraw consent and request data deletion at any time</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary font-semibold">•</span>
                        <span>All data processing complies with applicable privacy laws and regulations</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary font-semibold">•</span>
                        <span>We reserve the right to update these terms with appropriate notice</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Optional Recruiter Contact */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 10 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="pt-3 border-t border-border/50"
                >
                  <div className="flex items-start space-x-3 p-3 bg-card rounded-lg border border-border/50 shadow-sm">
                <Checkbox
                  id="optInRecruiterContact"
                  checked={optInRecruiterContact}
                  onCheckedChange={(checked) => setOptInRecruiterContact(Boolean(checked))}
                      className="mt-0.5 border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground focus-visible:ring-primary"
                  aria-labelledby="optInLabel"
                />
                <div className="grid gap-1.5 leading-none flex-1">
                       <Label htmlFor="optInRecruiterContact" id="optInLabel" className="font-semibold text-foreground flex items-center cursor-pointer text-sm">
                         <Users className="h-3 w-3 mr-2 text-primary flex-shrink-0" />
                         Connect with vTech Recruitment Team
                   </Label>
                       <p className="text-xs text-muted-foreground leading-relaxed">
                         <strong>Optional:</strong> If you're actively seeking career opportunities, you may opt to share your 
                         contact information with our internal recruitment team for potential job opportunities.
                   </p>
                </div>
              </div>
                </motion.div>

                {/* Bottom indicator when scrolled */}
                {hasScrolledToBottom && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="flex justify-center pt-2"
                  >
                    <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-500/10 border border-green-500/20 rounded-full text-xs text-green-600 font-medium">
                      <CheckCircle className="h-3 w-3" />
                      <span>Ready to accept terms</span>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>

          {/* Fixed Footer */}
          <DialogFooter className="p-4 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border-t border-border/50 flex-shrink-0">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: hasScrolledToBottom ? 1 : 0, y: hasScrolledToBottom ? 0 : 10 }}
              transition={{ duration: 0.5 }}
              className="w-full space-y-2"
            >
              <Button
                onClick={() => onAgree(optInRecruiterContact)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 w-full text-sm py-3 h-auto rounded-lg shadow-lg font-semibold transition-all duration-300"
                disabled={!hasScrolledToBottom}
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Accept Terms & Enter vTech Labs
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Please read all terms before accepting
              </p>
            </motion.div>
          </DialogFooter>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentDialog;
